import { DialogFrequencySwitch } from "@/components/billing/dialog-frequency-switch";
import { ButtonTeamBillingPortal } from "@/components/team/button-team-billing-portal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import {
  formatCurrency,
  formatDateNumeric,
  userInitials,
} from "@/lib/format-amount";
import {
  openSupportConversation,
  openSupportEmail,
  useSupportMessengerReady,
} from "@/lib/support";
import { cn } from "@/lib/utils";
import {
  LicenseFrequencyDTO,
  LicenseStockDTO,
  Nullable,
  TeamBillingPriceDTO,
  TeamInvitationDTO,
  TeamLicenseInventoryDTO,
  TeamUserDTO,
} from "@shaarp/common";
import {
  ChevronDownIcon,
  InfoIcon,
  MailIcon,
  PlusIcon,
} from "lucide-react";
import { useState } from "react";

type Frequency = "monthly" | "annual";

/** Above this seat count we render a progress bar instead of individual pills. */
const SEAT_STRIP_MAX = 10;
/** Order frequencies so the monthly plan surfaces first. */
const FREQUENCY_ORDER: Frequency[] = ["monthly", "annual"];

const formatMoney = (lang: string, amount: number): string =>
  formatCurrency(amount, "EUR", lang, { maximumFractionDigits: 0 });

type SubscriptionRow = {
  frequency: Frequency;
  productName: string;
  price: Nullable<TeamBillingPriceDTO>;
  stock: LicenseStockDTO;
  scheduledQuantity: Nullable<number>;
  periodEndsAt: Nullable<Date>;
  cancelAtPeriodEnd: boolean;
  assignedUsers: TeamUserDTO[];
  pendingInvitations: TeamInvitationDTO[];
}

type SeatStripProps = {
  total: number;
  assigned: number;
  ghostCount: number;
  assignedUsers: TeamUserDTO[];
  pendingInvitations: TeamInvitationDTO[];
  readOnly: boolean;
  onAssign: () => void;
}

/**
 * Renders one pill per seat: an avatar for each assigned user, a filled pill for
 * assigned-but-unresolved seats (e.g. pending invitations), and a dashed "+" pill
 * for each available seat. Falls back to a progress bar for large plans.
 */
const SeatStrip = ({
  total,
  assigned,
  ghostCount,
  assignedUsers,
  pendingInvitations,
  readOnly,
  onAssign,
}: SeatStripProps) => {
  const { t, i18n } = useTranslation();

  const fillClass = "bg-majorelle text-majorelle-foreground";

  if (total > SEAT_STRIP_MAX) {
    const assignedPct = total > 0 ? (assigned / total) * 100 : 0;
    const ghostPct = total > 0 ? (ghostCount / total) * 100 : 0;
    const availablePct = Math.max(100 - assignedPct - ghostPct, 0);
    const bar = (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium tabular-nums text-muted-foreground">
          {assigned} / {total}
        </span>
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-secondary">
          {assigned > 0 && (
            <div
              className={cn("h-full", fillClass)}
              style={{ width: `${assignedPct}%` }}
            />
          )}
          {availablePct > 0 && (
            <div className="h-full" style={{ width: `${availablePct}%` }} />
          )}
          {ghostCount > 0 && (
            <div
              className="h-full bg-destructive/30 border-l border-destructive/50"
              style={{ width: `${ghostPct}%` }}
            />
          )}
        </div>
      </div>
    );

    if (readOnly) {
      return <div className="w-full max-w-xs">{bar}</div>;
    }

    return (
      <button
        type="button"
        onClick={onAssign}
        aria-label={t("team.licenseManagement.inviteWithLicense")}
        className="flex w-full max-w-xs flex-col rounded-md text-left transition-opacity hover:opacity-80"
      >
        {bar}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {Array.from({ length: total }).map((_, idx) => {
        if (idx < assigned) {
          const user = assignedUsers[idx];
          // Seats beyond the resolved users are held by pending invitations,
          // which count toward the assigned total but have no user account yet.
          const invitation = user
            ? undefined
            : pendingInvitations[idx - assignedUsers.length];

          // Flagged from the seat's own scheduled unassignment, so the ring
          // lands on the member actually designated for removal rather than on
          // whoever happens to sit at the end of the strip. Invitations are
          // never flagged: a selected invitation is cancelled on the spot, so
          // one still showing here is not part of the pending decrease.
          const isScheduledForRemoval = !!user?.scheduledUnassignAt;

          const avatar = (
            <Avatar
              className={cn(
                "h-8 w-8 border border-background",
                isScheduledForRemoval &&
                  "ring-1 ring-destructive ring-offset-1 ring-offset-background"
              )}
            >
              {user?.imageUrl && (
                <AvatarImage src={user.imageUrl} alt={user.fullName} />
              )}
              <AvatarFallback className={cn("text-xs font-medium", fillClass)}>
                {user ? (
                  userInitials(user)
                ) : invitation ? (
                  <MailIcon className="h-3.5 w-3.5" />
                ) : (
                  ""
                )}
              </AvatarFallback>
            </Avatar>
          );

          const userLabel = user ? user.fullName?.trim() || user.email : null;
          const tooltipLabel = user
            ? user.scheduledUnassignAt
              ? t("team.licenseManagement.seatScheduledRemovalTooltip", {
                  name: userLabel,
                  date: formatDateNumeric(
                    user.scheduledUnassignAt,
                    i18n.language
                  ),
                })
              : userLabel
            : invitation
              ? t("team.licenseManagement.pendingInvitationTooltip", {
                  email: invitation.email,
                })
              : null;

          if (!tooltipLabel) {
            return <span key={idx}>{avatar}</span>;
          }

          return (
            <Tooltip key={idx}>
              <TooltipTrigger asChild>{avatar}</TooltipTrigger>
              <TooltipContent>
                <p>{tooltipLabel}</p>
              </TooltipContent>
            </Tooltip>
          );
        }

        const placeholderClass =
          "border-muted-foreground/40 text-muted-foreground";

        if (readOnly) {
          return (
            <div
              key={idx}
              className={cn(
                "h-8 w-8 rounded-full border border-dashed",
                placeholderClass
              )}
            />
          );
        }

        return (
          <button
            key={idx}
            type="button"
            onClick={onAssign}
            aria-label={t("team.licenseManagement.inviteWithLicense")}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full border border-dashed transition-colors hover:border-majorelle hover:text-majorelle",
              placeholderClass
            )}
          >
            <PlusIcon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
};

type SeatCountProps = {
  assigned: number;
  total: number;
  available: number;
  hasScheduledDecrease: boolean;
  scheduledTotal: number;
  /** Seats dropped at the next renewal by the pending decrease. */
  removedCount: number;
  formattedDate: string;
}

/** Right-aligned "assigned / total" with an availability status underneath. */
const SeatCount = ({
  assigned,
  total,
  available,
  hasScheduledDecrease,
  scheduledTotal,
  removedCount,
  formattedDate,
}: SeatCountProps) => {
  const { t } = useTranslation();

  const status = (() => {
    if (hasScheduledDecrease && formattedDate) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* The icon is what tells the manager a tooltip exists — the red
                count alone gave no affordance to hover. */}
            <span className="inline-flex items-center gap-1 text-destructive font-medium cursor-help">
              {available === 0
                ? t("team.licenseManagement.full")
                : t("team.licenseManagement.available", { count: available })}
              <InfoIcon className="h-3 w-3 shrink-0" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>
              {t("team.licenseManagement.scheduledTooltip", {
                count: scheduledTotal,
                date: formattedDate,
              })}
            </p>
            <p className="mt-1 text-muted-foreground">
              {t("team.licenseManagement.scheduledTooltipRule", {
                count: removedCount,
                date: formattedDate,
              })}
            </p>
          </TooltipContent>
        </Tooltip>
      );
    }
    if (available === 0) {
      return (
        <span className="font-medium text-muted-foreground">
          {t("team.licenseManagement.full")}
        </span>
      );
    }
    return (
      <span className="text-green-600 dark:text-green-400">
        {t("team.licenseManagement.available", { count: available })}
      </span>
    );
  })();

  return (
    <div className="text-right leading-tight">
      <div className="text-sm tabular-nums text-muted-foreground">
        {assigned} / {total}
      </div>
      <div className="text-xs">{status}</div>
    </div>
  );
};

type OverviewBarProps = {
  assigned: number;
  total: number;
}

const OverviewBar = ({ assigned, total }: OverviewBarProps) => {
  const assignedPct = total > 0 ? Math.min((assigned / total) * 100, 100) : 0;

  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-secondary">
      <div
        className="h-full rounded-full bg-majorelle transition-all"
        style={{ width: `${assignedPct}%` }}
      />
    </div>
  );
};

type SubscriptionActionsProps = {
  frequency: Frequency;
  cancelAtPeriodEnd: boolean;
  onManage: () => void;
  onCancel: () => void;
  onResume: () => void;
  onSwitch: () => void;
}

const SubscriptionActions = ({
  frequency,
  cancelAtPeriodEnd,
  onManage,
  onCancel,
  onResume,
  onSwitch,
}: SubscriptionActionsProps) => {
  const { t } = useTranslation();

  const canSwitch = frequency === "monthly";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          {t("team.licenseManagement.manage")}
          <ChevronDownIcon className="ml-1 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          {t("team.licenseManagement.manage")}
        </DropdownMenuLabel>
        <DropdownMenuItem onSelect={onManage}>
          {frequency === "monthly"
            ? t("team.licenseManagement.manageMonthly")
            : t("team.licenseManagement.manageAnnual")}
        </DropdownMenuItem>

        {!cancelAtPeriodEnd && canSwitch && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onSwitch}>
              {t("team.licenseManagement.switchMonthlyToAnnual")}
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />
        {cancelAtPeriodEnd ? (
          <DropdownMenuItem onSelect={onResume}>
            {frequency === "monthly"
              ? t("team.licenseManagement.resumeMonthly")
              : t("team.licenseManagement.resumeAnnual")}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onSelect={onCancel}
            className="text-destructive focus:text-destructive"
          >
            {frequency === "monthly"
              ? t("team.licenseManagement.cancelMonthly")
              : t("team.licenseManagement.cancelAnnual")}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

type StatBlockProps = {
  label: string;
  children: React.ReactNode;
}

const StatBlock = ({ label, children }: StatBlockProps) => (
  <div className="flex flex-col gap-1.5 bg-card px-6 py-4">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    {children}
  </div>
);

type CardLicenseInventoryProps = {
  inventory: TeamLicenseInventoryDTO;
  onManageMonthly: () => void;
  onManageAnnual: () => void;
  onCancelMonthly: () => void;
  onCancelAnnual: () => void;
  onResumeMonthly: () => void;
  onResumeAnnual: () => void;
  onSwitchConfirmed: (updatedInventory: TeamLicenseInventoryDTO) => void;
  /** Team members, used to render an avatar on each assigned license. */
  users?: TeamUserDTO[];
  /** Pending invitations, used to render a tooltip on seats awaiting acceptance. */
  invitations?: TeamInvitationDTO[];
  /** Opens the invite dialog with the given price pre-selected (empty seat click). */
  onInvite?: (priceId: string) => void;
  readOnly?: boolean;
  /**
   * Free (admin-managed) plan: seats are assignable but the manager cannot buy,
   * change or cancel them, and no billing / frequency information is shown.
   */
  freePlan?: boolean;
}

/**
 * Loading placeholder for {@link CardLicenseInventory}. Mirrors the card's
 * header / stats band / rows structure so the layout doesn't shift once the
 * inventory resolves.
 */
export const CardLicenseInventorySkeleton = () => (
  <Card className="overflow-hidden p-0">
    {/* Header */}
    <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
      <div className="space-y-2">
        <Skeleton className="h-4 w-44" />
        <Skeleton className="h-3.5 w-64" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-8 w-36 rounded-md" />
        <Skeleton className="h-8 w-32 rounded-md" />
      </div>
    </div>

    {/* Stats band */}
    <div className="grid gap-px border-t bg-border sm:grid-cols-3">
      {Array.from({ length: 3 }).map((_, idx) => (
        <div key={idx} className="space-y-2 bg-card px-6 py-4">
          <Skeleton className="h-2.5 w-24" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>

    {/* Rows */}
    <div className="divide-y border-t">
      {Array.from({ length: 2 }).map((_, idx) => (
        <div key={idx} className="flex items-center gap-4 px-6 py-4">
          <div className="flex w-64 items-center gap-3">
            <Skeleton className="h-9 w-9 rounded-md" />
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-40" />
            </div>
          </div>
          <Skeleton className="h-8 flex-1 max-w-[180px] rounded-full" />
          <Skeleton className="h-8 w-16 rounded-md" />
        </div>
      ))}
    </div>
  </Card>
);

/**
 * License inventory card.
 * Renders a header, a stats band (occupancy, monthly cost, unused seats) and one
 * row per active subscription (frequency) with per-seat avatars.
 */
export const CardLicenseInventory = ({
  inventory,
  onManageMonthly,
  onManageAnnual,
  onCancelMonthly,
  onCancelAnnual,
  onResumeMonthly,
  onResumeAnnual,
  onSwitchConfirmed,
  users,
  invitations,
  onInvite,
  readOnly = false,
  freePlan = false,
}: CardLicenseInventoryProps) => {
  const { t, i18n } = useTranslation();
  const isMessengerReady = useSupportMessengerReady();

  // Free plans expose seat assignment only — never the buy / manage / cancel
  // billing controls nor any monthly/annual distinction.
  const showBilling = !readOnly && !freePlan;

  type SwitchState = {
    from: Frequency;
    to: Frequency;
  } | null;

  const [switchDialogState, setSwitchDialogState] = useState<SwitchState>(null);

  const priceFor = (frequency: Frequency): Nullable<TeamBillingPriceDTO> =>
    inventory.prices.find(
      (p) =>
        p.licenceType === "professional" &&
        p.interval === (frequency === "monthly" ? "month" : "year")
    ) ?? null;

  const freqData = (frequency: Frequency): LicenseFrequencyDTO =>
    frequency === "monthly" ? inventory.monthly : inventory.annual;

  const totals = (() => {
    const m = inventory.monthly;
    const a = inventory.annual;
    const purchased = m.pro.purchased + a.pro.purchased;
    const assigned = m.pro.assigned + a.pro.assigned;
    const available = m.pro.available + a.pro.available;
    return { purchased, assigned, available };
  })();

  const hasAnySubscription = totals.purchased > 0;
  const hasAnnualSubscription = inventory.annual.pro.purchased > 0;

  const rows: SubscriptionRow[] = [];
  for (const frequency of FREQUENCY_ORDER) {
    const data = freqData(frequency);
    const stock = data.pro;
    if (stock.purchased === 0) continue;

    const price = priceFor(frequency);

    rows.push({
      frequency,
      productName:
        price?.productName ??
        t("team.licenseManagement.tiers.professional.name"),
      price,
      stock,
      scheduledQuantity: data.scheduledPro,
      periodEndsAt: data.currentPeriodEndsAt,
      cancelAtPeriodEnd: data.cancelAtPeriodEnd,
      assignedUsers: price
        ? (users ?? []).filter((u) => u.priceId === price.id)
        : [],
      pendingInvitations: price
        ? (invitations ?? []).filter(
            (inv) => inv.status === "pending" && inv.priceId === price.id
          )
        : [],
    });
  }

  // Monthly cost sums the monthly licenses only, so it reflects what is actually
  // billed each month. Annual licenses are reported separately in annualCost and
  // are never annualized into this figure (mixing frequencies is confusing).
  const monthlyCost =
    (priceFor("monthly")?.amount ?? 0) * inventory.monthly.pro.purchased;

  // Annual cost sums the full yearly amount of annual licenses only; monthly
  // licenses are excluded entirely.
  const annualCost =
    (priceFor("annual")?.amount ?? 0) * inventory.annual.pro.purchased;

  // The next charge is the soonest upcoming renewal invoice across every active
  // subscription, with any promotion discount applied. Tax is not exposed by the
  // inventory API, so the figure is expressed excluding tax.
  const nextCharge = (() => {
    const candidates: {
      total: number;
      gross: number;
      percentOff: Nullable<number>;
      date: Date;
    }[] = [];
    for (const frequency of FREQUENCY_ORDER) {
      const data = freqData(frequency);
      if (data.cancelAtPeriodEnd || !data.currentPeriodEndsAt) continue;
      const gross = (priceFor(frequency)?.amount ?? 0) * data.pro.purchased;
      if (gross <= 0) continue;
      const net = data.percentOff ? gross * (1 - data.percentOff / 100) : gross;
      candidates.push({
        total: net,
        gross,
        percentOff: data.percentOff,
        date: new Date(data.currentPeriodEndsAt),
      });
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => a.date.getTime() - b.date.getTime());
    return candidates[0];
  })();

  const hasNextChargeDiscount =
    !!nextCharge && !!nextCharge.percentOff && nextCharge.percentOff > 0;

  const buyMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          className="bg-majorelle hover:bg-majorelle/90 text-white"
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          {t("team.licenseManagement.buyLicense")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onManageMonthly}>
          {t("team.licenseManagement.buyMonthlyLicenses")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onManageAnnual}>
          {t("team.licenseManagement.buyAnnualLicenses")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      <Card className="overflow-hidden p-0">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 px-6 pt-5 pb-4">
          <div className="space-y-1.5">
            <h2 className="text-base font-semibold leading-tight">
              {t("team.licenseManagement.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("team.licenseManagement.description")}
            </p>
          </div>
          {showBilling && (
            <div className="flex flex-wrap items-center gap-2">
              <ButtonTeamBillingPortal
                variant="outline"
                size="sm"
                text={t("team.licenseManagement.managePaymentsInvoices")}
              />
              {hasAnySubscription && buyMenu}
            </div>
          )}
          {/* Free plans have no self-serve controls at all, so the card would
              otherwise leave the manager with no way to change seat count. */}
          {!readOnly && freePlan && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  // Without a booted messenger `openSupportConversation` is a
                  // no-op, and the hint's only affordance would do nothing at
                  // all — fall back to the mailbox, as `SupportMessage` does.
                  onClick={() => {
                    if (!isMessengerReady) {
                      openSupportEmail();
                      return;
                    }
                    openSupportConversation(
                      t("team.licenseManagement.freePlanSupportPrefill")
                    );
                  }}
                  className="flex items-center gap-1.5 rounded-md text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <InfoIcon className="h-3.5 w-3.5 shrink-0" />
                  {t("team.licenseManagement.freePlanSupportHint")}
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{t("team.licenseManagement.freePlanSupportTooltip")}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {hasAnySubscription ? (
          /* Stats band */
          <div
            className={cn(
              "grid gap-px border-t bg-border",
              freePlan
                ? "sm:grid-cols-1"
                : hasAnnualSubscription
                  ? "sm:grid-cols-2 lg:grid-cols-4"
                  : "sm:grid-cols-3"
            )}
          >
            <StatBlock label={t("team.licenseManagement.occupancyLabel")}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold tabular-nums leading-none">
                    {totals.assigned}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {t("team.licenseManagement.occupiedOutOf", {
                      count: totals.purchased,
                    })}
                  </span>
                </span>
                {totals.available > 0 ? (
                  <span className="text-xs font-medium text-green-600 dark:text-green-400">
                    {t("team.licenseManagement.licensesFree", {
                      count: totals.available,
                    })}
                  </span>
                ) : (
                  <span className="text-xs font-medium text-muted-foreground">
                    {t("team.licenseManagement.noLicensesFree")}
                  </span>
                )}
              </div>
              <OverviewBar assigned={totals.assigned} total={totals.purchased} />
            </StatBlock>

            {!freePlan && (
              <>
                <StatBlock label={t("team.licenseManagement.monthlyCostLabel")}>
                  <span className="text-2xl font-bold tabular-nums leading-none">
                    {formatMoney(i18n.language, monthlyCost)}
                  </span>
                </StatBlock>

                {hasAnnualSubscription && (
                  <StatBlock label={t("team.licenseManagement.annualCostLabel")}>
                    <span className="text-2xl font-bold tabular-nums leading-none">
                      {formatMoney(i18n.language, annualCost)}
                    </span>
                  </StatBlock>
                )}

                <StatBlock label={t("team.licenseManagement.nextChargeLabel")}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold tabular-nums leading-none">
                      {formatMoney(i18n.language, nextCharge?.total ?? 0)}
                    </span>
                    {hasNextChargeDiscount && (
                      <span className="text-sm font-medium tabular-nums text-muted-foreground line-through">
                        {formatMoney(i18n.language, nextCharge!.gross)}
                      </span>
                    )}
                  </div>
                  {hasNextChargeDiscount && (
                    <span className="text-xs font-medium text-green-600 dark:text-green-400">
                      {t("team.licenseManagement.nextChargeDiscount", {
                        percent: nextCharge!.percentOff,
                      })}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {nextCharge
                      ? t("team.licenseManagement.nextChargeOn", {
                          date: formatDateNumeric(nextCharge.date, i18n.language),
                        })
                      : t("team.licenseManagement.noUpcomingCharge")}
                  </span>
                </StatBlock>
              </>
            )}
          </div>
        ) : (
          /* Empty state */
          <div className="flex flex-col items-center justify-center gap-4 border-t px-6 py-16 text-center">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                {t("team.licenseManagement.emptyStateTitle")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("team.licenseManagement.emptyStateDescription")}
              </p>
            </div>
            {showBilling && buyMenu}
          </div>
        )}

        {/* Subscription rows */}
        {hasAnySubscription && (
          <div className="divide-y border-t">
            {rows.map((row) => {
              const effectiveScheduled = row.cancelAtPeriodEnd
                ? 0
                : row.scheduledQuantity;
              const hasScheduledDecrease =
                effectiveScheduled !== null &&
                effectiveScheduled !== undefined &&
                effectiveScheduled < row.stock.purchased;
              const ghostCount = hasScheduledDecrease
                ? row.stock.purchased - effectiveScheduled!
                : 0;

              const frequencyLabel =
                row.frequency === "monthly"
                  ? t("team.licenseManagement.monthly")
                  : t("team.licenseManagement.annual");
              const perLicense = t(
                row.frequency === "monthly"
                  ? "team.licenseManagement.perLicenseMonth"
                  : "team.licenseManagement.perLicenseYear",
                { amount: formatMoney(i18n.language, row.price?.amount ?? 0) }
              );
              const renewal = row.periodEndsAt
                ? t("team.licenseManagement.renewShort", {
                    date: formatDateNumeric(row.periodEndsAt, i18n.language),
                  })
                : "";

              const onManage =
                row.frequency === "monthly" ? onManageMonthly : onManageAnnual;
              const onAssign = () =>
                row.price ? onInvite?.(row.price.id) : onManage();

              return (
                <div
                  key={row.frequency}
                  className="flex flex-wrap items-center gap-4 px-6 py-4"
                >
                  <div className="flex w-56 shrink-0 items-center gap-3">
                    <div className="min-w-0 space-y-0.5">
                      <p className="text-sm font-semibold leading-tight">
                        {freePlan
                          ? row.productName
                          : `${row.productName} · ${frequencyLabel}`}
                      </p>
                      {!freePlan && (
                        <p className="text-xs leading-tight text-muted-foreground">
                          {perLicense}
                        </p>
                      )}
                      {!freePlan && renewal && (
                        <p
                          className={cn(
                            "text-xs leading-tight text-muted-foreground",
                            row.cancelAtPeriodEnd && "text-destructive"
                          )}
                        >
                          {renewal}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="min-w-0 flex-1">
                    <SeatStrip
                      total={row.stock.purchased}
                      assigned={row.stock.assigned}
                      ghostCount={ghostCount}
                      assignedUsers={row.assignedUsers}
                      pendingInvitations={row.pendingInvitations}
                      readOnly={readOnly}
                      onAssign={onAssign}
                    />
                  </div>

                  <div className="flex shrink-0 items-center gap-4">
                    <SeatCount
                      assigned={row.stock.assigned}
                      total={row.stock.purchased}
                      available={row.stock.available}
                      hasScheduledDecrease={hasScheduledDecrease}
                      scheduledTotal={row.stock.purchased}
                      removedCount={ghostCount}
                      formattedDate={formatDateNumeric(
                        row.periodEndsAt,
                        i18n.language
                      )}
                    />
                    {showBilling && (
                      <SubscriptionActions
                        frequency={row.frequency}
                        cancelAtPeriodEnd={row.cancelAtPeriodEnd}
                        onManage={onManage}
                        onCancel={
                          row.frequency === "monthly"
                            ? onCancelMonthly
                            : onCancelAnnual
                        }
                        onResume={
                          row.frequency === "monthly"
                            ? onResumeMonthly
                            : onResumeAnnual
                        }
                        onSwitch={() =>
                          setSwitchDialogState(
                            row.frequency === "monthly"
                              ? { from: "monthly", to: "annual" }
                              : { from: "annual", to: "monthly" }
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {!readOnly && switchDialogState && (
        <DialogFrequencySwitch
          fromFrequency={switchDialogState.from}
          toFrequency={switchDialogState.to}
          onClose={() => setSwitchDialogState(null)}
          onConfirmed={(updated) => {
            setSwitchDialogState(null);
            onSwitchConfirmed(updated);
          }}
        />
      )}
    </>
  );
};
